import { BucketMovementType } from '@/integrations/agreements/client';

/**
 * O que o pedido guarda: quantas caçambas o transportador coloca e quantas retira.
 *
 * O gerador não pede troca: retirada e colocação são operações separadas, mesmo no mesmo pedido
 * e na mesma data. A troca continua existindo no Hub, e as antigas seguem na listagem.
 */
export interface BucketQuantities {
  placement: number;
  removal: number;
}

export type BucketQuantityField = keyof BucketQuantities;

/** Ordem do envio e da tela: o que sai antes do que entra. */
export const BUCKET_QUANTITY_FIELDS: BucketQuantityField[] = ['removal', 'placement'];

/** O que a pessoa digitou vira uma quantidade: inteiro, sem negativo. */
export function toBucketCount(value: string | number): number {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function countBucketOperations({ placement, removal }: BucketQuantities): number {
  return placement + removal;
}

export const BUCKET_FIELD_TYPES: Record<BucketQuantityField, BucketMovementType> = {
  placement: BucketMovementType.PLACEMENT,
  removal: BucketMovementType.REMOVAL,
};

const NOUNS: Record<BucketQuantityField | 'swap', [string, string]> = {
  swap: ['troca', 'trocas'],
  removal: ['retirada', 'retiradas'],
  placement: ['colocação', 'colocações'],
};

/**
 * "2 retiradas e 1 colocação". A troca só aparece ao descrever o que já existe — as agendadas
 * pelo Hub e as antigas; o gerador não pede troca.
 */
export function describeBucketQuantities(quantities: BucketQuantities & { swap?: number }): string {
  const parts = (['swap', ...BUCKET_QUANTITY_FIELDS] as const)
    .filter((field) => (quantities[field] ?? 0) > 0)
    .map((field) => `${quantities[field]} ${NOUNS[field][quantities[field] === 1 ? 0 : 1]}`);

  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}` : parts[0] ?? '';
}
